import {
	PartConfigurationBomApi,
	PartConfigurationBomApi_FormInitData,
} from "src/api/generated/erp/configurator/api/partConfigurationBomApi.ts";
import i18n from "i18next";
import { PartConfigurationBomLine } from "src/api/generated/erp/db/types/tables/partConfigurationBomLine.ts";
import { SitePartQueryApi } from "src/api/generated/erp/parts/sitePart/api/sitePartQueryApi.ts";
import { DeepPartial } from "react-hook-form";
import { requireRule } from "src/components/common/forms/validation.ts";
import { FormCommonProps, FormResult } from "src/components/common/forms/types.ts";
import { PartConfigurationView } from "src/api/generated/erp/db/types/tables/partConfigurationView.ts";
import { FormNumberField } from "src/components/common/forms/fields/FormNumberField.tsx";
import { FormAsyncSelectField } from "src/components/common/forms/fields/FormAsyncSelectField.tsx";
import { getPartLongName } from "src/components/views/erp/erpUtils.ts";
import { FetchAsyncOptionParams } from "src/components/common/inputFields/AsyncSelectField.tsx";
import { PartView } from "src/api/generated/erp/db/types/tables/partView.ts";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { PartConfigurationBomLineView } from "src/api/generated/erp/db/types/tables/partConfigurationBomLineView.ts";
import { FormEnumSelectField } from "src/components/common/forms/fields/FormEnumSelectField.tsx";
import { getAcquisitionMethodLabels } from "src/api/generated/erp/db/types/enums/acquisitionMethod.ts";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { PartConfigurationBomLineConfiguratorView } from "src/components/views/erp/configurator/configuratorForm/impl/PartConfigurationBomLineConfiguratorView.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { ConfigurationManualEditContext } from "src/api/generated/erp/configurator/manualEdit/configurationManualEditContext.ts";
import { applySelectedBomLinePartId } from "src/components/views/erp/configurator/inspecting/partConfigurationBomLineFormUtils.ts";

export interface PartConfigurationBomLineFormProps extends FormCommonProps<number> {
	partConfiguration: PartConfigurationView;
	existingBomLine: PartConfigurationBomLineView | undefined;
	refreshSourceView?: () => void;
	manualEditContext: ConfigurationManualEditContext;
}

interface FormValues extends Omit<PartConfigurationBomLine, "bomLinePartId"> {
	bomLinePart: PartView;
}

interface SubmitResult {
	partConfigurationBomLineId: number;
	linePartIsConfigurable: boolean;
}

export const PartConfigurationBomLineForm = (props: PartConfigurationBomLineFormProps) => {
	const {
		partConfiguration,
		existingBomLine,
		onFormEdited,
		onCompleted: onCompletedProp,
		refreshSourceView,
		manualEditContext,
	} = props;
	const { openDialog } = useGenericDialog();

	return (
		<AsyncForm
			fetch={() =>
				PartConfigurationBomApi.getFormInitData({
					partConfigurationId: partConfiguration.partConfigurationId,
				})
			}
			getDefaultValues={getDefaultValues}
			submit={submitForm}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.configurator.partConfigurationBomLine.form"),
				data: {
					partConfigurationId: partConfiguration.partConfigurationId,
					partConfigurationBomLineId: existingBomLine?.partConfigurationBomLineId ?? null,
				},
			}}
			render={(params) => <FormContent {...props} {...params} />}
		/>
	);

	function getDefaultValues({ defaultReferenceNo }: PartConfigurationBomApi_FormInitData): DeepPartial<FormValues> {
		return existingBomLine ?
				{
					...existingBomLine,
					bomLinePart: {
						partId: existingBomLine.bomLinePartId,
						partUnit: existingBomLine.linePartUnitName,
					},
				}
			:	{
					partConfigurationId: partConfiguration.partConfigurationId,
					referenceNo: defaultReferenceNo,
					quantity: 1,
					acquisitionMethod: "WAREHOUSE",
					unitWorkingTime: 0,
					setUpTime: 0,
				};
	}

	async function submitForm(values: FormValues): Promise<SubmitResult> {
		const bomLineId = await PartConfigurationBomApi.saveBomLine({
			partConfigurationBomLine: applySelectedBomLinePartId(values),
			manualEditContext,
		});
		return {
			partConfigurationBomLineId: bomLineId,
			linePartIsConfigurable: values.bomLinePart.isConfigurable,
		};
	}

	function onCompleted(result: FormResult<SubmitResult>) {
		if (result.type === "success") {
			const { partConfigurationBomLineId, linePartIsConfigurable } = result.value;
			onCompletedProp({ type: "success", value: partConfigurationBomLineId });

			if (!existingBomLine && linePartIsConfigurable) {
				openBomLineConfigurator(partConfigurationBomLineId);
			}
		} else {
			onCompletedProp(result);
		}
	}

	function openBomLineConfigurator(partConfigurationBomLineId: number) {
		openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
			title: i18n.t("configure"),
			size: "lg",
			content: (
				<PartConfigurationBomLineConfiguratorView
					partConfigurationBomLineId={partConfigurationBomLineId}
					manualEditContext={manualEditContext}
					closeDialog={closeDialog}
					refreshSourceView={refreshSourceView}
					onDataDirtyStateChanged={onDataDirtyStateChanged}
				/>
			),
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.partConfigurationBomLineConfigurator.dialog"),
				title: i18n.t("configure"),
				data: {
					partConfigurationId: partConfiguration.partConfigurationId,
					partConfigurationBomLineId: partConfigurationBomLineId,
				},
			},
		}));
	}
};

interface FormContentProps
	extends
		PartConfigurationBomLineFormProps,
		AsyncFormContentParams<PartConfigurationBomApi_FormInitData, FormValues> {}

const FormContent = ({
	partConfiguration,
	existingBomLine,
	control,
	watch,
	data: { operationOptions, bomLineGroupOptions },
}: FormContentProps) => {
	const currentBomLinePart = watch("bomLinePart");

	return (
		<>
			<FormNumberField
				control={control}
				name={"referenceNo"}
				label={i18n.t("reference_no")}
				rules={requireRule()}
				type={"integer"}
			/>
			<FormAsyncSelectField
				control={control}
				name={"bomLinePart"}
				formValueType={"option"}
				label={i18n.t("part")}
				rules={requireRule()}
				disabled={existingBomLine?.bomLinePartConfigurationId != null}
				fetchOptions={({ currentSelection, searchQuery }: FetchAsyncOptionParams<number>) =>
					SitePartQueryApi.getPartSelectionOptions({
						currentPartId: currentSelection,
						searchQuery: searchQuery,
						siteId: partConfiguration.siteId,
					})
				}
				getOptionKey={(part) => part.partId}
				getOptionLabel={getPartLongName}
			/>
			<FormNumberField
				control={control}
				name={"quantity"}
				label={`${i18n.t("quantity")} (${currentBomLinePart?.partUnit ?? ""})`}
				rules={requireRule()}
			/>
			<FormEnumSelectField
				control={control}
				name={"acquisitionMethod"}
				label={i18n.t("acquisition_method")}
				options={getAcquisitionMethodLabels()}
				rules={requireRule()}
			/>
			<FormSelectField
				control={control}
				name={"partConfigurationOperationId"}
				label={i18n.t("operation")}
				options={operationOptions}
				getOptionKey={(o) => o.partConfigurationOperationId}
				getOptionLabel={(o) => o.operationLongName ?? ""}
			/>
			<FormSelectField
				control={control}
				name={"bomLineGroupId"}
				label={i18n.t("bom_line_group")}
				options={bomLineGroupOptions}
				getOptionKey={(o) => o.bomLineGroupId}
				getOptionLabel={(o) => o.name}
			/>
			<FormTextField control={control} name={"note"} label={i18n.t("note")} multiline />
		</>
	);
};
